import { Button, Chip, formatBaht, Icons } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { customerBackendApi } from "../features/api/backend.ts";
import { type MessageKey, useT } from "../features/i18n/index.ts";
import { cartTotalMinor } from "../features/order/cart.ts";
import {
  type LiveOrderStatus,
  ORDER_STEPS,
  useSession,
} from "../features/session/session-context.tsx";

export const Route = createFileRoute("/orders/$orderId")({
  component: OrderStatusScreen,
});

const ORDER_STATUSES: LiveOrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
  "cancelled",
  "rejected",
];

function toLiveStatus(status: string): LiveOrderStatus | null {
  return ORDER_STATUSES.includes(status as LiveOrderStatus) ? (status as LiveOrderStatus) : null;
}

const TERMINAL_COPY: Partial<
  Record<LiveOrderStatus, { titleKey: MessageKey; bodyKey: MessageKey }>
> = {
  cancelled: { titleKey: "order.cancelledTitle", bodyKey: "order.cancelledBody" },
  rejected: { titleKey: "order.rejectedTitle", bodyKey: "order.rejectedBody" },
  served: { titleKey: "order.step.served", bodyKey: "order.servedBody" },
};

function OrderStatusScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { order, tableCode, advanceOrderStatus, syncOrderStatus, backendMode } = useSession();

  // Backend mode: poll the caller's orders and reflect the real status on the
  // live screen while an order is still active.
  const ordersQuery = useQuery({
    queryKey: ["customer", "orders", "me"],
    queryFn: () =>
      customerBackendApi?.listMyOrders() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendMode && order !== null,
    refetchInterval: 5_000,
  });

  const polledStatus = backendMode ? ordersQuery.data?.[0]?.status : undefined;
  useEffect(() => {
    if (!backendMode || !polledStatus) return;
    const next = toLiveStatus(polledStatus);
    if (next) syncOrderStatus(next);
  }, [backendMode, polledStatus, syncOrderStatus]);

  if (!order) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-5 px-8 text-center">
        <h1 className="h-display text-[1.5rem]">{t("order.none")}</h1>
        <Button onClick={() => navigate({ to: "/menu" })}>{t("order.goToMenu")}</Button>
      </div>
    );
  }

  const currentIdx = ORDER_STEPS.findIndex((s) => s.id === order.status);
  const total = cartTotalMinor(order.lines);
  const terminalCopy = TERMINAL_COPY[order.status];

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/home" })}
      >
        <Icons.IconBack size={18} /> {t("nav.home")}
      </button>

      <div className="jod-kicker">{t("common.table", { code: tableCode })}</div>
      <h1 className="h-display mt-1 text-[1.7rem]">{t("order.title")}</h1>

      {terminalCopy ? (
        <div
          className={`glass mt-5 rounded-xl p-4 ${order.status === "served" ? "border border-lime/30" : "border border-red-400/30"}`}
        >
          <div className="flex items-center gap-2 font-display font-semibold text-cream">
            {order.status === "served" ? (
              <Icons.IconCheck size={17} />
            ) : (
              <Icons.IconFlag size={17} />
            )}
            {t(terminalCopy.titleKey)}
          </div>
          <p className="mt-1 text-cream-3 text-xs leading-relaxed">{t(terminalCopy.bodyKey)}</p>
          {order.staffNote ? (
            <p className="mt-2 text-gold text-xs">
              {t("order.reason", { reason: order.staffNote })}
            </p>
          ) : null}
        </div>
      ) : null}

      <ol className="mt-6 space-y-0">
        {ORDER_STEPS.map((step, i) => {
          const done = currentIdx >= 0 && i < currentIdx;
          const active = i === currentIdx && !["cancelled", "rejected"].includes(order.status);
          const reached = done || active || order.status === "served";
          return (
            <li key={step.id} className="flex gap-3.5">
              <div className="flex flex-col items-center">
                <span
                  className={`grid size-8 place-items-center rounded-full border ${
                    reached
                      ? "border-gold bg-[oklch(79%_0.12_80_/_14%)] text-gold"
                      : "border-hairline text-cream-4"
                  }`}
                >
                  {done || order.status === "served" ? (
                    <Icons.IconCheck size={16} />
                  ) : active ? (
                    <span className="jod-live-dot" style={{ background: "currentColor" }} />
                  ) : (
                    <Icons.IconClock size={15} />
                  )}
                </span>
                {i < ORDER_STEPS.length - 1 ? (
                  <span
                    className={`my-1 w-px flex-1 ${reached ? "bg-gold/40" : "bg-[var(--hairline)]"}`}
                    style={{ minHeight: 28 }}
                  />
                ) : null}
              </div>
              <div className={`pb-6 ${reached ? "text-cream" : "text-cream-3"}`}>
                <div className="font-display font-semibold">{t(step.labelKey)}</div>
                {active ? (
                  <div className="text-cream-3 text-xs">
                    {customerBackendApi ? t("order.activeBackend") : t("order.activeMock")}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="glass mt-2 space-y-2 rounded-lg p-4">
        {order.lines.map((l) => (
          <div key={l.id} className="flex items-center justify-between text-sm">
            <span className="text-cream-2">
              {l.nameTh ?? l.name} × {l.quantity}
            </span>
            <span className="text-cream-3">{formatBaht(l.priceMinor * l.quantity)}</span>
          </div>
        ))}
        <div className="border-[var(--hairline)] border-t pt-2" />
        <div className="flex items-center justify-between">
          <span className="text-cream-2">{t("order.total")}</span>
          <span className="font-display font-semibold text-gold">{formatBaht(total)}</span>
        </div>
      </div>

      {backendMode ? (
        <div className="glass mt-4 rounded-lg p-4">
          <div className="jod-kicker mb-2">{t("order.statusKicker")}</div>
          <p className="text-cream-3 text-xs">{t("order.statusAuto")}</p>
        </div>
      ) : (
        <div className="glass mt-4 rounded-lg p-4">
          <div className="jod-kicker mb-2">Mock staff controls</div>
          <p className="mb-3 text-cream-3 text-xs">{t("order.mockHint")}</p>
          <div className="flex flex-wrap gap-2">
            {ORDER_STEPS.map((step) => (
              <Chip
                key={step.id}
                on={order.status === step.id}
                onClick={() => advanceOrderStatus(step.id)}
              >
                {step.id}
              </Chip>
            ))}
            <Chip
              on={order.status === "cancelled"}
              onClick={() => advanceOrderStatus("cancelled", "guest_cancelled_mock")}
            >
              cancelled
            </Chip>
            <Chip
              on={order.status === "rejected"}
              onClick={() => advanceOrderStatus("rejected", "item_unavailable_mock")}
            >
              rejected
            </Chip>
          </div>
        </div>
      )}

      <Button variant="ghost" className="mt-5 w-full" onClick={() => navigate({ to: "/around" })}>
        {t("order.viewAround")}
      </Button>
    </div>
  );
}
